/**
 * Single source of truth for primary nav links (desktop strip, mobile drawer, bottom bar).
 * Keep order in sync: new sections should be added here once.
 */

/**
 * Secondary links shown in the login / account drawer (not the desktop top strip).
 * Mobile hamburger drawer still lists the full main nav.
 * About + Mobile App stay in the top navbar; contact icons stay in the login panel footer.
 */
export const LOGIN_DRAWER_NAV_IDS = ['nri', 'contact'];

/**
 * Store / Live TV tabs are gated by feature flags (WEB_STORE, LIVE_TV).
 * Pass `{ storeEnabled, liveTvEnabled }` from FeatureFlagsContext.
 */
export function getMainNavItems(pathname, flags = {}) {
  const storeEnabled = Boolean(flags.storeEnabled);
  const liveTvEnabled = Boolean(flags.liveTvEnabled);
  const p = pathname || '';
  const isPoojaPage = p === '/poojas' || p === '/past' || p.startsWith('/pooja/');
  const isStorePage = p === '/store';
  const isAstrologyPage = p === '/astrology';
  const isLiveTvPage = p === '/live-tv';
  const isNriPage = p === '/nri';
  const isBlogPage = p === '/blogs' || p.startsWith('/blogs/') || p.startsWith('/blog/');
  const isMobileAppPage = p === '/mobile-app';
  const isContactPage = p === '/contact';

  const items = [
    {
      id: 'home',
      to: '/',
      end: true,
      labelKey: 'nav.home',
      active: p === '/',
      linkKind: 'navlink',
    },
    {
      id: 'pooja',
      to: '/poojas',
      labelKey: 'nav.pooja',
      active: isPoojaPage,
      linkKind: 'link',
    },
    {
      id: 'store',
      to: '/store',
      labelKey: 'nav.store',
      active: isStorePage,
      linkKind: 'link',
    },
    {
      id: 'mobileApp',
      to: '/mobile-app',
      labelKey: 'nav.mobileApp',
      active: isMobileAppPage,
      linkKind: 'link',
    },
    {
      id: 'astrology',
      to: '/astrology',
      labelKey: 'nav.astrology',
      active: isAstrologyPage,
      linkKind: 'link',
    },
    {
      id: 'liveTv',
      to: '/live-tv',
      labelKey: 'nav.liveTv',
      active: isLiveTvPage,
      linkKind: 'link',
    },
    {
      id: 'blogs',
      to: '/blogs',
      labelKey: 'nav.blogs',
      active: isBlogPage,
      linkKind: 'link',
    },
    {
      id: 'about',
      to: '/about',
      end: true,
      labelKey: 'nav.aboutUs',
      active: p === '/about',
      linkKind: 'navlink',
    },
    {
      id: 'nri',
      to: '/nri',
      labelKey: 'nav.nri',
      active: isNriPage,
      linkKind: 'link',
    },
    {
      id: 'contact',
      to: '/contact',
      labelKey: 'nav.contactUs',
      active: isContactPage,
      linkKind: 'link',
    },
  ];

  return items.filter((item) => {
    if (item.id === 'temple') return false;
    if (item.id === 'store' && !storeEnabled) return false;
    if (item.id === 'liveTv' && !liveTvEnabled) return false;
    return true;
  });
}

/** Desktop top strip — excludes links that live in the login drawer. */
export function getDesktopTopNavItems(pathname, flags = {}) {
  return getMainNavItems(pathname, flags).filter((item) => !LOGIN_DRAWER_NAV_IDS.includes(item.id));
}

/** NRI / Contact for login-drawer Explore (About + Mobile App stay in top nav). */
export function getLoginDrawerNavItems(pathname, flags = {}) {
  const byId = Object.fromEntries(getMainNavItems(pathname, flags).map((item) => [item.id, item]));
  return LOGIN_DRAWER_NAV_IDS.map((id) => byId[id]).filter(Boolean);
}

/** Mobile bottom bar only — subset of main nav (Mobile App is in top nav). */
export const MOBILE_BOTTOM_PRIMARY_IDS = ['home', 'pooja', 'astrology', 'blogs'];
